/**
 * Intelligent address parser for various formats
 */

interface ParsedAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
}

/**
 * Parse a multi-line address string into structured components
 * Handles various formats from Harvest and other sources
 */
export function parseAddress(addressString: string): ParsedAddress {
  const result: ParsedAddress = {
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
  };

  if (!addressString || addressString.trim() === '') {
    return result;
  }

  // Split by common line breaks and clean up
  const lines = addressString
    .split(/[\r\n]+/)
    .map(line => line.trim())
    .filter(line => line.length > 0);

  if (lines.length === 0) {
    return result;
  }

  // Last line typically contains city, state, zip
  const lastLine = lines[lines.length - 1];
  const cityStateZip = parseCityStateZip(lastLine);

  // If we successfully parsed city/state/zip, use it
  if (cityStateZip.city || cityStateZip.state || cityStateZip.zip) {
    result.city = cityStateZip.city;
    result.state = cityStateZip.state;
    result.zip = cityStateZip.zip;

    // Remaining lines are address
    const addressLines = lines.slice(0, -1);
    result.addressLine1 = addressLines[0] || '';
    result.addressLine2 = addressLines.slice(1).join(', ') || '';
  } else {
    // Couldn't parse city/state/zip, just use lines as address
    result.addressLine1 = lines[0] || '';
    result.addressLine2 = lines.slice(1).join(', ') || '';
  }

  return result;
}

/**
 * Parse city, state, and zip from a single line
 * Handles formats like:
 * - "Birmingham, AL 35226"
 * - "San Diego CA 92120"
 * - "Grawn, MI, 49637"
 * - "New York, NY 10001-1234"
 */
function parseCityStateZip(line: string): { city: string; state: string; zip: string } {
  const result = { city: '', state: '', zip: '' };

  if (!line) return result;

  // Try to match: City, ST ZIP or City ST ZIP or City, ST, ZIP
  // ZIP can be 5 digits or 5+4 format (12345-6789)
  const patterns = [
    // "Birmingham, AL 35226" or "Birmingham, AL, 35226"
    /^(.+?),\s*([A-Z]{2})[,\s]+(\d{5}(?:-\d{4})?)$/,
    // "San Diego CA 92120" (no comma before state)
    /^(.+?)\s+([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/,
    // "City, State Zip" with extra spaces
    /^(.+?),?\s*([A-Z]{2})\s*,?\s*(\d{5}(?:-\d{4})?)$/,
  ];

  for (const pattern of patterns) {
    const match = line.match(pattern);
    if (match) {
      result.city = match[1].trim();
      result.state = match[2].trim();
      result.zip = match[3].trim();
      return result;
    }
  }

  // If no pattern matched, try to at least extract state and zip
  const stateZipMatch = line.match(/([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/);
  if (stateZipMatch) {
    result.state = stateZipMatch[1];
    result.zip = stateZipMatch[2];
    // Everything before state/zip is city
    const cityPart = line.substring(0, line.indexOf(stateZipMatch[0])).trim();
    result.city = cityPart.replace(/,\s*$/, ''); // Remove trailing comma
  }

  return result;
}

/**
 * Test the parser with sample addresses
 */
export function testAddressParser() {
  const testCases = [
    "2401 Pinson Valley Pkwy\r\nBirmingham, AL 35226",
    "0000 Battle creek Rd suite 200\r\nSan Diego CA 92120\r\n",
    "5930 US 31 South \r\nGrawn, MI, 49637",
    "123 Main St\nApt 4B\nNew York, NY 10001",
    "456 Oak Avenue\nSuite 100\nLos Angeles, CA 90210-1234",
  ];

  console.log('=== Address Parser Test ===');
  testCases.forEach((address, index) => {
    const parsed = parseAddress(address);
    console.log(`\nTest ${index + 1}:`);
    console.log('Input:', address);
    console.log('Parsed:', parsed);
  });
}

